/**
 * New Year bunting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearBuntingPack: ToilePack = {
    slug: 'new-year-bunting',
    category: 'season',
    name: 'New Year bunting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'patterned', label: 'Patterned flags', subject: 'a sheet of triangle bunting flags in confetti and deco patterns, one flag lettered "2026" among them, dashed cut lines'},
        {key: 'letters', label: 'Letter flags', subject: 'a sheet of triangle bunting flags each holding one letter of "CHEERS" in bold, dashed cut lines'},
        {key: 'mini', label: 'Cake flags', subject: 'a sheet of tiny triangle cake-topper flags in confetti and deco patterns, one flag lettered "2026" among them, dashed cut lines'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a landscape sheet with a gold border and an even margin, the flags arranged in a row, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a landscape sheet with a confetti border and a white margin, the flags arranged in a row, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a landscape sheet with a wide margin, the flags arranged in a row, print-ready.'},
    ],
    directions: ['for a birthday instead', 'spelling out the year', 'as a mini cake topper'],
    directionHint: 'try: for a birthday instead',
    seo: {
        title: 'New Year Bunting: Patterned, Letter & Cake Flags',
        metaDescription: "Nine New Year bunting sheets — patterned triangles, 'CHEERS' letter flags, and mini cake flags — in black-and-gold, confetti, minimal styles.",
        h1: 'New Year bunting: patterned, letter, and cake flags',
        tagline: 'Nine sheets, three flag sets, three styles each — dashed cut lines around every flag.',
        intro: "Three New Year bunting sheets — patterned triangle flags, letter flags spelling 'CHEERS', and tiny cake-topper flags — each in black-and-gold, confetti-party, and minimal styles, with dashed cut lines throughout. Ask for a different word or a longer string, and the same bunting grammar carries it.",
        useCases: [
            "a New Year's Eve party garland",
            'a mantel or window display',
            'a dessert table decoration',
            'a countdown photo backdrop',
        ],
        faq: [
            {q: 'How do I hang these after printing?', a: 'Cut along the dashed lines and string or tape the flags along ribbon or twine — no punched holes are included.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single landscape page; the flags print at a size ready to cut by hand.'},
            {q: 'Can I get a different word or longer set?', a: 'Yes — the direction field takes any variation, including a different word or a longer string.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
